import React, { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import chartColors from '../../../utils/chartColors';
import { SensorLatest } from '../../../services/api';
import { SensorItem } from '../../../utils/sensorProperties';

interface SensorTagsProps {
  sensorDetails: SensorLatest;
  isEditing: boolean;
  sensorItem: SensorItem | undefined;
  setSensorItem: (value: SensorItem) => void;
}

function SensorTags({
  sensorDetails,
  isEditing,
  sensorItem,
  setSensorItem,
}: SensorTagsProps): JSX.Element {
  const sensorTags = sensorDetails.tags ?? [];
  const [tags, setTags] = useState<string[]>(sensorTags ?? []);
  const [inputValue, setInputValue] = useState('');

  const getChartColor = (tag: string) => chartColors[sensorTags.indexOf(tag) % chartColors.length];

  const handleAddTag = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !tags.includes(trimmedValue)) {
      const allTags = [...tags, trimmedValue];
      const item = { ...sensorItem };
      item.tags = allTags;
      setSensorItem(item);
      setTags([...tags, trimmedValue]);
      setInputValue('');
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToDelete);
    const item = { ...sensorItem };
    item.tags = updatedTags;
    setSensorItem(item);
    setTags(updatedTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <ListItem key="tags">
      <Grid size={{ sm: 6, xs: 6 }}>
        <ListItemText primary="Tags" />
      </Grid>
      <Grid size={{ sm: 6, xs: 6 }}>
        {/* show existing tags */}
        {sensorTags.length > 0 &&
          !isEditing &&
          sensorTags.map((tag) => (
            <span
              key={tag}
              style={{
                background: getChartColor(tag),
                padding: '5px 10px',
                borderRadius: '16px',
                marginRight: '5px',
              }}
            >
              {tag}
            </span>
          ))}
        {isEditing && (
          <Box>
            <Stack spacing={3}>
              <Box>
                <TextField
                  fullWidth
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  variant="standard"
                  placeholder="Type tag name and press Enter"
                />
              </Box>

              <Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {tags.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No tags added yet
                    </Typography>
                  ) : (
                    tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleDeleteTag(tag)}
                        color="secondary"
                        variant="outlined"
                      />
                    ))
                  )}
                </Stack>
              </Box>
            </Stack>
          </Box>
        )}
      </Grid>
    </ListItem>
  );
}

export default SensorTags;
